import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { SingleValue } from 'react-select';
import { Box, Typography, TextField } from '@mui/material';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../../i18n/constants/keys';
import {
  QuestionSearch,
  SubSkill,
} from '../../../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../../../utils/types/react_select_types';

type FilterProps = {
  skillList: SelectOptions[];
  levelList: SelectOptions[];
  subSkillList: SubSkill[] | undefined;
  jobTitleList: SelectOptions[] | undefined;
  searchObject: QuestionSearch;
  setSearchObject: (searchObject: QuestionSearch) => void;
};

const customStyles = {
  container: (base: any) => ({
    ...base,
    width: '200px',
  }),
};

export const Filter: React.FC<FilterProps> = ({
  skillList,
  levelList,
  subSkillList,
  jobTitleList,
  searchObject,
  setSearchObject,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const QF = ADMIN_PAGES_KEYS.jobOpenings.questionFilter;
  const [subSkills1, setSubSkills1] = useState<SelectOptions[]>();
  const [subSkills2, setSubSkills2] = useState<SelectOptions[]>();
  const [subSkills3, setSubSkills3] = useState<SelectOptions[]>();

  const returnSubSkills = (parent: string | undefined) => {
    let subSkills: SelectOptions[] = [];
    subSkillList?.forEach((subSkill: SubSkill) => {
      if (subSkill.parentSkillId === parent) {
        subSkills.push({ label: subSkill.subskillName, value: subSkill.id });
      }
    });

    return subSkills;
  };

  const handleSelectChange = (
    selectedValue: SingleValue<SelectOptions>,
    action: any,
  ) => {
    if (action.name === 'skillId') {
      if (selectedValue) {
        setSubSkills1(() => returnSubSkills(selectedValue.value));
      }
      setSubSkills2([]);
      setSubSkills3([]);
      setSearchObject({
        ...searchObject,
        [action.name]: selectedValue ? selectedValue.value : '',
        subSkill1Id: '',
        subSkill2Id: '',
        subSkill3Id: '',
      });
    } else if (action.name === 'subSkill1Id') {
      if (selectedValue) {
        setSubSkills2(() => returnSubSkills(selectedValue.value));
      }
      setSubSkills3([]);
      setSearchObject({
        ...searchObject,
        [action.name]: selectedValue ? selectedValue.value : '',
        subSkill2Id: '',
        subSkill3Id: '',
      });
    } else if (action.name === 'subSkill2Id') {
      if (selectedValue) {
        setSubSkills3(() => returnSubSkills(selectedValue.value));
      }
      setSearchObject({
        ...searchObject,
        [action.name]: selectedValue ? selectedValue.value : '',
        subSkill3Id: '',
      });
    } else {
      setSearchObject({
        ...searchObject,
        [action.name]: selectedValue ? selectedValue.value : '',
      });
    }
  };

  const onSearchTermChange = (event: ChangeEvent<HTMLInputElement>) => {
    let searchTitle = event.target.value;
    setSearchObject({
      ...searchObject,
      questionTitle: searchTitle,
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: '200px' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
        {t(QF.applyFilters)}
      </Typography>
      <Select
        placeholder={t(QF.selectSkill)}
        name="skillId"
        options={skillList}
        onChange={handleSelectChange}
        isClearable={true}
        styles={customStyles}
      />
      {searchObject.skillId !== null && searchObject.skillId.length > 0 && (
        <Select
          placeholder={t(QF.selectSubSkill1)}
          name="subSkill1Id"
          options={subSkills1}
          onChange={handleSelectChange}
          isClearable={true}
          styles={customStyles}
        />
      )}
      {searchObject.subSkill1Id.length > 0 && (
        <Select
          placeholder={t(QF.selectSubSkill2)}
          name="subSkill2Id"
          options={subSkills2}
          onChange={handleSelectChange}
          isClearable={true}
          styles={customStyles}
        />
      )}
      {searchObject.subSkill2Id.length > 0 && (
        <Select
          placeholder={t(QF.selectSubSkill3)}
          name="subSkill3Id"
          options={subSkills3}
          onChange={handleSelectChange}
          isClearable={true}
          styles={customStyles}
        />
      )}
      <Select
        placeholder={t(QF.selectSkillLevel)}
        name="levelId"
        options={levelList}
        onChange={handleSelectChange}
        isClearable={true}
        styles={customStyles}
      />
      <Select
        placeholder={t(QF.selectJobTitle)}
        name="jobTitle"
        options={jobTitleList}
        onChange={handleSelectChange}
        isClearable={true}
        styles={customStyles}
      />
      <TextField
        size="small"
        placeholder={t(QF.searchQuestions)}
        onChange={onSearchTermChange}
        fullWidth
      />
    </Box>
  );
};
export default Filter;
